import { useCallback, useEffect, useRef, type RefObject } from "react";
import * as d3 from "d3";
import { useMap } from "react-map-gl/mapbox";
import type { MapRef } from "react-map-gl/mapbox";

import type { MapEdge, MapNode } from "@thalamos/aspire-common";

import { useIsMobile } from "../hooks/useIsMobile.js";

/** Colours for each node type — designed for dark map backgrounds */
/** A node with its projected screen coordinates (the d3 datum on each path). */
type NodeDatum = MapNode & { x: number; y: number };

const NODE_COLOURS: Record<MapNode["type"], string> = {
  "nhs-trust": "#6366f1",
  "nhs-hospital": "#38bdf8",
  "local-authority": "#14b8a6",
  police: "#22c55e",
  "place-of-safety": "#f59e0b",
  cqc: "#a855f7",
  "cqc-provider": "#ec4899",
  independent: "#8b5cf6",
  prison: "#64748b",
  "incident-location": "#ef4444",
  "combined-facility": "url(#combined-gradient)",
  "gp-practice": "#10b981",
  pcn: "#06b6d4",
};

const EDGE_COLOURS: Record<MapEdge["type"], string> = {
  "aspire-work-routing": "rgba(59, 130, 246, 0.4)",
  "vision-incident-share": "rgba(34, 197, 94, 0.4)",
  "vision-pos-arrival": "rgba(245, 158, 11, 0.4)",
  "vision-incident-location": "rgba(239, 68, 68, 0.4)",
  "cqc-share": "rgba(236, 72, 153, 0.4)",
  "independent-share": "rgba(139, 92, 246, 0.4)",
  "pos-transfer": "rgba(251, 191, 36, 0.6)",
  "admission-gp": "rgba(16, 185, 129, 0.4)",
  "admission-pcn": "rgba(6, 182, 212, 0.4)",
  "gp-pcn": "rgba(6, 182, 212, 0.2)",
};

interface D3OverlayProps {
  nodes: MapNode[];
  edges: MapEdge[];
  mapRef: RefObject<MapRef | null>;
  onNodeHover: (
    node: MapNode | null,
    screenPos?: { x: number; y: number },
  ) => void;
}

/** Density multiplier: shrinks nodes when many are on screen */
let densityScale = 1;

function nodeRadius(volume: number, zoom: number): number {
  // Base radius from log of volume
  const base = Math.max(
    3,
    Math.min(14, 2 + Math.log2(Math.max(1, volume)) * 1.4),
  );
  // Scale down when zoomed out: at zoom 5.5 → 0.5x, at zoom 10 → 1.5x
  const zoomScale = Math.max(0.4, Math.min(1.8, 0.2 + zoom * 0.12));
  return base * zoomScale * densityScale;
}

/** Max visible nodes before we disable interaction */
const INTERACTION_THRESHOLD = 200;

function edgeWidth(weight: number, zoom: number): number {
  const base = Math.max(0.5, Math.min(5, Math.log2(Math.max(1, weight)) * 0.6));
  const zoomScale = Math.max(0.3, Math.min(1.5, 0.1 + zoom * 0.1));
  return base * zoomScale;
}

/** Compute a quadratic Bézier control point offset perpendicular to the edge midpoint */
function bezierControlPoint(
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  curvature = 0.3,
): [number, number] {
  const mx = (x1 + x2) / 2;
  const my = (y1 + y2) / 2;
  const dx = x2 - x1;
  const dy = y2 - y1;
  const len = Math.sqrt(dx * dx + dy * dy);
  if (len === 0) return [mx, my];
  // Perpendicular offset
  const nx = -dy / len;
  const ny = dx / len;
  return [mx + nx * len * curvature, my + ny * len * curvature];
}

export function D3Overlay({
  nodes,
  edges,
  mapRef,
  onNodeHover,
}: D3OverlayProps) {
  const svgRef = useRef<SVGSVGElement>(null);
  const { current: map } = useMap();
  const isMobile = useIsMobile();

  // Focus state: hover sets transient focus, click locks it
  const hoveredIdRef = useRef<string | null>(null);
  const lockedIdRef = useRef<string | null>(null);

  /** The currently focused node id (locked takes priority over hovered) */
  const getFocusedId = () => lockedIdRef.current ?? hoveredIdRef.current;

  const project = useCallback(
    (lng: number, lat: number): [number, number] | null => {
      const m = mapRef.current?.getMap();
      if (!m) return null;
      const pt = m.project([lng, lat]);
      return [pt.x, pt.y];
    },
    [mapRef],
  );

  const render = useCallback(() => {
    const svg = svgRef.current;
    if (!svg || !mapRef.current) return;

    const container = svg.parentElement;
    if (!container) return;

    const width = container.clientWidth;
    const height = container.clientHeight;
    svg.setAttribute("width", String(width));
    svg.setAttribute("height", String(height));

    const sel = d3.select(svg);

    const zoom = mapRef.current?.getZoom() ?? 5.5;

    // Viewport culling bounds (with generous margin)
    const margin = 80;
    const inViewport = (x: number, y: number) =>
      x > -margin && x < width + margin && y > -margin && y < height + margin;

    // Level-of-detail: hide low-importance node types when zoomed out
    // Tier 0 (always visible): nhs-trust, nhs-hospital, place-of-safety,
    //   combined-facility, local-authority, police, cqc, cqc-provider, prison
    // Tier 1 (zoom >= 7): independent doctors with volume > 5
    // Tier 2 (zoom >= 8): gp-practice, pcn, incident-location, remaining independents
    const TIER_1_TYPES = new Set<MapNode["type"]>(["independent"]);
    const TIER_2_TYPES = new Set<MapNode["type"]>([
      "gp-practice",
      "pcn",
      "incident-location",
    ]);

    /** Simple deterministic hash of a string → 0..1 */
    function hashFraction(s: string): number {
      let h = 0;
      for (let i = 0; i < s.length; i++) {
        h = (h * 31 + s.charCodeAt(i)) | 0;
      }
      return ((h >>> 0) % 1000) / 1000;
    }

    function isNodeVisible(node: MapNode): boolean {
      if (TIER_2_TYPES.has(node.type)) {
        if (zoom >= 8) return true;
        // Subsample at lower zoom: show a fraction based on node id hash
        const frac = hashFraction(node.id);
        if (node.type === "pcn") {
          // PCNs: 90% at zoom 6–7, all at zoom 7–8
          return zoom >= 7 || frac < 0.9;
        }
        // GP / incident-location: 30% at zoom 6–7, 90% at zoom 7–8
        if (zoom >= 7) return frac < 0.9;
        return frac < 0.3;
      }
      if (TIER_1_TYPES.has(node.type))
        return zoom >= 8 || (zoom >= 7 && node.volume > 5);
      return true;
    }

    // Build projected node positions
    // Detect co-located nodes and apply jitter only when zoomed in (>= 9)
    const jitterStrength = zoom >= 10 ? 1 : zoom >= 9 ? zoom - 9 : 0;
    let nodeOffsets: Map<string, { dx: number; dy: number }> | null = null;

    if (jitterStrength > 0) {
      const geoKey = (n: MapNode) => `${n.lat.toFixed(4)},${n.lng.toFixed(4)}`;
      const geoGroups = new Map<string, MapNode[]>();
      for (const n of nodes) {
        const key = geoKey(n);
        const group = geoGroups.get(key);
        if (group) group.push(n);
        else geoGroups.set(key, [n]);
      }
      nodeOffsets = new Map();
      const offsetRadius = 12 * jitterStrength;
      for (const group of geoGroups.values()) {
        if (group.length < 2) continue;
        const angleStep = (2 * Math.PI) / group.length;
        for (const [i, node] of group.entries()) {
          nodeOffsets.set(node.id, {
            dx: Math.cos(angleStep * i) * offsetRadius,
            dy: Math.sin(angleStep * i) * offsetRadius,
          });
        }
      }
    }

    const projectedNodes = nodes
      .map((node) => {
        if (!isNodeVisible(node)) return null;
        const pt = project(node.lng, node.lat);
        if (!pt) return null;
        const x = pt[0] + (nodeOffsets?.get(node.id)?.dx ?? 0);
        const y = pt[1] + (nodeOffsets?.get(node.id)?.dy ?? 0);
        if (!inViewport(x, y)) return null;
        return { ...node, x, y };
      })
      .filter(Boolean) as (MapNode & { x: number; y: number })[];

    // Density-aware scaling: shrink nodes when there are many on screen
    const visibleCount = projectedNodes.length;
    densityScale =
      visibleCount > 500
        ? 0.5
        : visibleCount > 200
          ? 0.7
          : visibleCount > 100
            ? 0.85
            : 1;

    const dense = visibleCount > INTERACTION_THRESHOLD;

    const nodeById = new Map(projectedNodes.map((n) => [n.id, n]));

    // --- Edges ---
    // Minimum pixel length to render an edge — cull short edges when zoomed out
    const minEdgePx = zoom < 7 ? 30 : zoom < 8 ? 15 : 0;

    const edgeData = edges
      .map((edge) => {
        const source = nodeById.get(edge.sourceId);
        const target = nodeById.get(edge.targetId);
        if (!source || !target) return null;

        // Use anonymised incident location when available
        let sx = source.x;
        let sy = source.y;
        if (edge.sourceLat != null && edge.sourceLng != null) {
          const pt = project(edge.sourceLng, edge.sourceLat);
          if (pt) {
            sx = pt[0];
            sy = pt[1];
          }
        }

        // Cull edges shorter than threshold (visual noise when zoomed out)
        if (minEdgePx > 0) {
          const dx = sx - target.x;
          const dy = sy - target.y;
          if (dx * dx + dy * dy < minEdgePx * minEdgePx) return null;
        }

        return { ...edge, source: { ...source, x: sx, y: sy }, target };
      })
      .filter(Boolean) as (MapEdge & {
      source: MapNode & { x: number; y: number };
      target: MapNode & { x: number; y: number };
    })[];

    const edgeSel = sel
      .selectAll<SVGPathElement, (typeof edgeData)[number]>("path.edge")
      .data(edgeData, (d) => `${d.sourceId}-${d.targetId}-${d.type}`);

    edgeSel.exit().remove();

    const edgeEnter = edgeSel
      .enter()
      .append("path")
      .attr("class", "edge")
      .attr("fill", "none")
      .attr("stroke-linecap", "round");

    edgeEnter
      .merge(edgeSel)
      .attr("d", (d) => {
        const [cx, cy] = bezierControlPoint(
          d.source.x,
          d.source.y,
          d.target.x,
          d.target.y,
          0.2,
        );
        return `M ${d.source.x} ${d.source.y} Q ${cx} ${cy} ${d.target.x} ${d.target.y}`;
      })
      .attr("stroke", (d) => EDGE_COLOURS[d.type])
      .attr("stroke-width", (d) => edgeWidth(d.weight, zoom));

    // --- Nodes (circles for most types, diamonds for LAs) ---
    const nodeSel = sel
      .selectAll<SVGPathElement, (typeof projectedNodes)[number]>("path.node")
      .data(projectedNodes, (d) => d.id);

    nodeSel.exit().remove();

    const nodeEnter = nodeSel
      .enter()
      .append("path")
      .attr("class", "node")
      .style("cursor", "pointer");

    nodeEnter
      .merge(nodeSel)
      .attr("transform", (d) => `translate(${d.x},${d.y})`)
      .attr("d", (d) => {
        const r = nodeRadius(d.volume, zoom);
        if (d.type === "local-authority") {
          // Diamond shape
          return `M 0 ${-r} L ${r} 0 L 0 ${r} L ${-r} 0 Z`;
        }
        // Circle approximation via arc path
        return `M ${-r} 0 A ${r} ${r} 0 1 1 ${r} 0 A ${r} ${r} 0 1 1 ${-r} 0 Z`;
      })
      .attr("fill", (d) => NODE_COLOURS[d.type])
      .attr("stroke", "#fff")
      .attr("stroke-width", 1)
      .attr("opacity", 0.9)
      // In dense mode the handlers are attached but no-op (pointer-events is
      // also disabled below), avoiding a null-vs-listener union d3's types reject.
      .on(
        "mouseenter",
        function (this: SVGPathElement, _event: Event, d: NodeDatum) {
          if (dense) return;
          onNodeHover(d, { x: d.x, y: d.y });
          if (!lockedIdRef.current) {
            hoveredIdRef.current = d.id;
            applyFocus(sel, edges);
          }
        },
      )
      .on("mouseleave", function (this: SVGPathElement) {
        if (dense) return;
        onNodeHover(null);
        if (!lockedIdRef.current) {
          hoveredIdRef.current = null;
          applyFocus(sel, edges);
        }
      })
      .on("click", function (this: SVGPathElement, event: Event, d: NodeDatum) {
        if (dense) return;
        event.stopPropagation();
        if (lockedIdRef.current === d.id) {
          lockedIdRef.current = null;
          hoveredIdRef.current = d.id;
        } else {
          lockedIdRef.current = d.id;
        }
        applyFocus(sel, edges);
      })
      .style("pointer-events", dense || isMobile ? "none" : "all");

    // Apply focus styling after layout
    applyFocus(sel, edges);

    // --- Labels (visible at higher zoom levels) ---
    const showLabels = zoom >= 11;

    if (showLabels) {
      const labelNodes = projectedNodes.filter(
        (n) =>
          n.type !== "incident-location" &&
          n.type !== "independent" &&
          n.type !== "gp-practice" &&
          n.type !== "pcn",
      );
      const labelSel = sel
        .selectAll<SVGTextElement, (typeof projectedNodes)[number]>(
          "text.label",
        )
        .data(labelNodes, (d) => d.id);

      labelSel.exit().remove();

      labelSel
        .enter()
        .append("text")
        .attr("class", "label")
        .attr("fill", "#e2e8f0")
        .attr("font-size", 10)
        .attr("font-family", "system-ui, sans-serif")
        .attr("text-anchor", "middle")
        .style("pointer-events", "none")
        .merge(labelSel)
        .attr("x", (d) => d.x)
        .attr("y", (d) => d.y - nodeRadius(d.volume, zoom) - 4)
        .text((d) => d.name);
    } else {
      sel.selectAll("text.label").remove();
    }
  }, [nodes, edges, project, mapRef, onNodeHover, isMobile]);

  /** Apply focus/dim styling based on the currently focused node */
  const applyFocus = useCallback(
    (
      sel: d3.Selection<SVGSVGElement, unknown, null, undefined>,
      edgeList: MapEdge[],
    ) => {
      const focusedId = getFocusedId();

      if (!focusedId) {
        // No focus — restore defaults
        sel
          .selectAll<SVGPathElement, MapEdge>("path.edge")
          .attr("stroke", (d) => EDGE_COLOURS[d.type])
          .attr("opacity", null);
        sel
          .selectAll<SVGPathElement, MapNode>("path.node")
          .attr("opacity", 0.9)
          .attr("stroke-width", 1);
        sel
          .selectAll<SVGTextElement, MapNode>("text.label")
          .attr("opacity", null);
        return;
      }

      // Build set of connected node ids
      const connectedIds = new Set<string>();
      connectedIds.add(focusedId);
      for (const e of edgeList) {
        if (e.sourceId === focusedId) connectedIds.add(e.targetId);
        if (e.targetId === focusedId) connectedIds.add(e.sourceId);
      }

      const DIM_EDGE = 0.06;
      const DIM_NODE = 0.15;

      sel
        .selectAll<SVGPathElement, MapEdge>("path.edge")
        .attr("opacity", (d) =>
          d.sourceId === focusedId || d.targetId === focusedId ? 1 : DIM_EDGE,
        )
        .attr("stroke", (d) =>
          d.sourceId === focusedId || d.targetId === focusedId
            ? EDGE_COLOURS[d.type]
            : "rgba(100, 116, 139, 0.2)",
        );

      sel
        .selectAll<SVGPathElement, MapNode>("path.node")
        .attr("opacity", (d) => (connectedIds.has(d.id) ? 1 : DIM_NODE))
        .attr("stroke-width", (d) => (d.id === focusedId ? 2.5 : 1));

      sel
        .selectAll<SVGTextElement, MapNode>("text.label")
        .attr("opacity", (d) => (connectedIds.has(d.id) ? 1 : DIM_NODE));
    },
    [],
  );

  // Re-render on map move / zoom
  useEffect(() => {
    const m = map?.getMap();
    if (!m) return;

    const handler = () => render();
    m.on("move", handler);
    m.on("zoom", handler);
    m.on("resize", handler);

    // Initial render
    render();

    return () => {
      m.off("move", handler);
      m.off("zoom", handler);
      m.off("resize", handler);
    };
  }, [map, render]);

  // Re-render when data changes
  useEffect(() => {
    render();
  }, [render]);

  // Click on background to unlock
  const handleBackgroundClick = useCallback(() => {
    if (lockedIdRef.current) {
      lockedIdRef.current = null;
      hoveredIdRef.current = null;
      const svg = svgRef.current;
      if (svg) applyFocus(d3.select(svg), edges);
    }
  }, [edges, applyFocus]);

  // Listen for map clicks to unlock focus (clicks on nodes are stopped via stopPropagation)
  useEffect(() => {
    const m = map?.getMap();
    if (!m) return;
    const handler = () => handleBackgroundClick();
    m.on("click", handler);
    return () => {
      m.off("click", handler);
    };
  }, [map, handleBackgroundClick]);

  // Forward wheel events through to the map canvas so scroll-zoom works
  // even when the cursor is over a node
  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;
    const handler = (e: WheelEvent) => {
      e.preventDefault();
      const canvas = map?.getMap()?.getCanvas();
      if (canvas) {
        canvas.dispatchEvent(new WheelEvent(e.type, e));
      }
    };
    svg.addEventListener("wheel", handler, { passive: false });
    return () => svg.removeEventListener("wheel", handler);
  }, [map]);

  return (
    <svg
      ref={svgRef}
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        pointerEvents: "none",
        zIndex: 10,
      }}
    >
      {/* On mobile: no pointer-events on nodes so all gestures pass to
          Mapbox. On desktop: nodes are interactive for hover/click. */}
      {!isMobile && <style>{`path.node { pointer-events: all; }`}</style>}
      <defs>
        <linearGradient
          id="combined-gradient"
          x1="0%"
          y1="0%"
          x2="100%"
          y2="0%"
        >
          <stop offset="50%" stopColor="#38bdf8" />
          <stop offset="50%" stopColor="#f59e0b" />
        </linearGradient>
      </defs>
    </svg>
  );
}
